import React, { FocusEvent, useState } from "react";
import {
  GoogleOAuthProvider,
  GoogleLogin,
  CredentialResponse,
} from "@react-oauth/google";
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { NavLink, useNavigate } from "react-router-dom";
import { usersApi } from "../../api";
import { appConfig } from "../../app-config";
import { configuration } from "../../config";
import {
  APP_ROUTES,
  ERROR_MESSAGES,
  IAuthResponse,
  IRegisterParams,
  IResponseError,
  SUCCESS_MESSAGES,
  VALID_EMAIL_REGEX,
  VALIDATION_MESSAGES,
} from "../../data-access";
import { FormInput, FormButton, BrandLogo, Turnstile } from "../../ui";
import {
  cookie,
  encryption,
  getStoredUtm,
  gtag,
  handleFailedRequest,
  notify,
  sanitizeEmail,
  updateExtensionCookies,
} from "../../util";
import "./registration.scss";
import { useDispatch } from "react-redux";
import { storeAuthActions } from "../../store/slices/auth";
import { storeBillingActions } from "../../store/slices/billing";
import { syncAuthWithExtension } from "../../util/auth-sync";
import { AuthTestimonials } from "../auth";

const validationScheme = yup
  .object({
    email: yup
      .string()
      .required("Please enter your email address")
      .matches(VALID_EMAIL_REGEX, VALIDATION_MESSAGES.INVALID_EMAIL),
  })
  .required();

export const Registration = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const form = useForm<IRegisterParams>({
    mode: "onBlur",
    defaultValues: {
      email: "",
    },
    resolver: yupResolver(validationScheme),
  });
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors, dirtyFields },
  } = form;

  const [isRequestProcessing, setIsRequestProcessing] =
    useState<boolean>(false);
  const [turnstileToken, setTurnstileToken] = useState<string>("");
  // Remounting the widget is what produces a fresh token. Calling Turnstile's
  // own reset() looked equivalent and shipped first, but production logs showed
  // the very same token being replayed for minutes afterwards - it never took
  // effect. Changing the key tears the widget down and builds a new one, which
  // cannot return a stale token.
  const [captchaKey, setCaptchaKey] = useState<number>(0);

  const handleFormSubmit = (formFields: IRegisterParams): void => {
    setIsRequestProcessing(true);
    usersApi
      .register({ ...formFields, turnstile_token: turnstileToken })
      .then(() => {
        notify("success", SUCCESS_MESSAGES.SIGN_UP);
        gtag("config", "AW-11007670916");
        // Funnel: email-magic-link requested, user not yet created. The
        // actual GA4 `sign_up` fires in auth.tsx once the link is clicked
        // and the account is provisioned.
        if ((window as any).gtag) {
          (window as any).gtag("event", "sign_up_started", { method: "email" });
        }
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => {
        setIsRequestProcessing(false);
        reset();
        // A Turnstile token is single-use: Cloudflare answers a replay with
        // `timeout-or-duplicate`. `reset()` above only clears the email field,
        // so without this the spent token stays in state and every retry after
        // a failed attempt fails verification until the page is reloaded.
        setTurnstileToken("");
        setCaptchaKey((key) => key + 1);
      });
  };

  const handleGoogleFormSubmit = (formFields: CredentialResponse): void => {
    setIsRequestProcessing(true);

    usersApi
      .googleAuthenticate(formFields.credential)
      .then((response: IAuthResponse) => {
        const authDetails = {
          access_token: response.access_token,
          refresh_token: response.refresh_token,
          user: response.user,
        };

        dispatch(storeAuthActions.setUserData(authDetails));

        syncAuthWithExtension("AUTH_LOGIN", {
          accessToken: response.access_token,
          refreshToken: response.refresh_token,
          user: response.user,
        });

        if (response.user.promocode) {
          dispatch(
            storeBillingActions.update({ discount: response.user.promocode })
          );
        }

        cookie.set("exSM", encryption.generateHash("authorized"));
        updateExtensionCookies(authDetails);

        // First-touch attribution (gclid + UTMs). Safe for returning users
        // who sign in via this button - the backend only stamps accounts
        // created within the last hour, write-once.
        const storedUtm = getStoredUtm();
        if (Object.keys(storedUtm).length > 0) {
          usersApi.saveAttribution(storedUtm).catch(() => undefined);
        }

        navigate(APP_ROUTES.dashboard);
      })
      .catch((error: IResponseError) => {
        handleFailedRequest(error.status, error.text, error.details);
      })
      .finally(() => setIsRequestProcessing(false));
  };

  const handleEmailInput = (e: FocusEvent<HTMLInputElement>): void => {
    const sanitizedEmail = sanitizeEmail(e?.target?.value);
    setValue("email", sanitizedEmail, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  // Only gate submission on the captcha when the widget is actually
  // configured, so a build without the site key behaves exactly as before.
  const captchaRequired = Boolean(process.env.REACT_APP_TURNSTILE_SITE_KEY);

  const isFormValid =
    Object.keys(dirtyFields).length > 0 &&
    !errors?.email?.message &&
    (!captchaRequired || Boolean(turnstileToken));

  return (
    <div className="registration-page">
      <BrandLogo />
      <AuthTestimonials />

      <div className="registration-page__form-side">
        <div className="registration-page__form-container">

          <div className="registration-page__header">
            <h1 className="registration-page__title">Create an account</h1>
            <p className="registration-page__subtitle">
              Start your <span>{appConfig.trialPeriodDuration}-day free trial</span> today
            </p>
          </div>

          <div className="registration-page__google-wrapper">
            <div className="registration-page__google-custom">
              <svg viewBox="0 0 24 24" width="20" height="20">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              <span>Continue with Google</span>
            </div>
            <div
              className={`registration-page__google-btn${isRequestProcessing ? " registration-page__google-btn--disabled" : ""}`}
            >
              <GoogleOAuthProvider
                clientId={`${process.env.REACT_APP_AUTH_CLIENT_ID}`}
              >
                <GoogleLogin
                  size="large"
                  width="400"
                  locale="en"
                  text="signup_with"
                  shape="rectangular"
                  onSuccess={(response: CredentialResponse) => {
                    handleGoogleFormSubmit(response);
                  }}
                  onError={() => {
                    notify("error", ERROR_MESSAGES.GENERIC);
                  }}
                />
              </GoogleOAuthProvider>
            </div>
          </div>

          <div className="registration-page__divider">
            <span>or register with email</span>
          </div>

          <form
            className="registration-page__form"
            onSubmit={handleSubmit(handleFormSubmit)}
          >
            <Controller
              control={control}
              name="email"
              render={({ field: { name, value, ref } }) => (
                <FormInput
                  ref={ref}
                  name={name}
                  value={value}
                  placeholder="Enter your email address"
                  error={errors?.email?.message}
                  disabled={isRequestProcessing}
                  onChange={handleEmailInput}
                />
              )}
            />
            <FormButton
              className="registration-page__submit-btn"
              theme="green-light"
              htmlType="submit"
              loading={isRequestProcessing}
              disabled={!isFormValid || isRequestProcessing}
            >
              Create account
            </FormButton>
          </form>

          <p className="registration-page__terms">
            By signing up, you agree to our{" "}
            <a href={configuration.termsLink} target="_blank" rel="noreferrer">
              Terms
            </a>{" "}
            &{" "}
            <a
              href={configuration.privacyPolicyLink}
              target="_blank"
              rel="noreferrer"
            >
              Privacy Policy
            </a>
          </p>

          {/* Sits outside <form> on purpose: it lives below the terms line by
              design, and the submit button already waits on turnstileToken via
              isFormValid, so the token does not need to be a form field. */}
          <Turnstile key={captchaKey} onVerify={setTurnstileToken} />

          <div className="registration-page__footer">
            Already have an account?
            <NavLink to={APP_ROUTES.login}>Sign in</NavLink>
          </div>
        </div>
      </div>
    </div>
  );
};
